import { useMemo } from 'react';

import type { CoverageDisplayStatus, OrganizedCoverages, PatientCoverage } from '../types';

import { useGetPatientCoveragesQuery } from './api';
import { getCoverageDisplayStatus, getPatientSelfPayFlag } from './helpers';

// Pure coverage logic lives in ./helpers (no API imports — see the note
// there); re-exported here so existing import sites keep working.
export * from './helpers';

/** Display statuses that occupy an editor slot (history rows do not). */
const SLOT_DISPLAY_STATUSES = new Set<CoverageDisplayStatus>(['active', 'pending', 'future']);

const DISPLAY_STATUS_SLOT_RANK: Record<CoverageDisplayStatus, number> = {
  active: 0,
  pending: 1,
  future: 2,
  expired: 3,
  cancelled: 4,
};

function pickSlot(
  coverages: PatientCoverage[],
  order: number,
): { current?: PatientCoverage; incoming?: PatientCoverage } {
  const inSlot = coverages
    .filter(
      c =>
        c.coverage_order === order && SLOT_DISPLAY_STATUSES.has(getCoverageDisplayStatus(c)),
    )
    .sort(
      (a, b) =>
        DISPLAY_STATUS_SLOT_RANK[getCoverageDisplayStatus(a)] -
        DISPLAY_STATUS_SLOT_RANK[getCoverageDisplayStatus(b)],
    );
  const current = inSlot[0];
  // Any additional selectable row in the slot is the incoming/successor
  // plan — a mid-entry one is 'pending' until its start date is set, and
  // it must stay visible in the editor rather than vanish on first save.
  const incoming = inSlot.find(c => c !== current);
  return { current, incoming };
}

/**
 * Hook to get patient coverages organized by order (primary, secondary, tertiary)
 *
 * Fetches with include_inactive so expired/cancelled plans are available as
 * `history` (GDN-2411); slot organization only considers active/pending rows.
 *
 * Pass `{ skip: true }` to hold the fetch when the patient id isn't known yet
 * (e.g. still-loading callers) — the hook must still be called unconditionally
 * to respect the Rules of Hooks.
 */
export function useOrganizedPatientCoverages(
  patientId: string,
  options?: { skip?: boolean },
) {
  const { data, isLoading, error, refetch } = useGetPatientCoveragesQuery(
    { patientId, includeInactive: true },
    { skip: options?.skip === true },
  );

  const organized = useMemo<OrganizedCoverages>(() => {
    if (!data?.coverages) {
      return {};
    }

    const primarySlot = pickSlot(data.coverages, 1);
    return {
      primary: primarySlot.current,
      incomingPrimary: primarySlot.incoming,
      secondary: pickSlot(data.coverages, 2).current,
      tertiary: pickSlot(data.coverages, 3).current,
    };
  }, [data]);

  const history = useMemo<PatientCoverage[]>(() => {
    if (!data?.coverages) return [];
    return data.coverages
      .filter(c => {
        const displayStatus = getCoverageDisplayStatus(c);
        return displayStatus === 'expired' || displayStatus === 'cancelled';
      })
      .sort((a, b) => (b.termination_date ?? '').localeCompare(a.termination_date ?? ''));
  }, [data]);

  return {
    coverages: data?.coverages || [],
    organized,
    history,
    coverageInForceToday: data?.coverage_in_force_today ?? null,
    nextCoverageGap: data?.next_coverage_gap ?? null,
    // Patient-level self-pay designation, flag-only since the GDN-3177
    // contract phase: the response-level is_self_pay is the sole source.
    // A designated patient may hold ZERO coverage rows — never infer
    // designation from a row's kind or existence.
    isSelfPay: Boolean(getPatientSelfPayFlag(data)),
    isLoading,
    error,
    refetch,
    total: data?.total || 0,
  };
}

/**
 * Hook to check if a patient has any active coverages
 */
export function useHasActiveCoverages(patientId: string) {
  const { data, isLoading } = useGetPatientCoveragesQuery({ patientId });

  const hasActive = useMemo(() => {
    if (!data?.coverages) return false;
    return data.coverages.some(c => getCoverageDisplayStatus(c) === 'active');
  }, [data]);

  return {
    hasActive,
    isLoading,
    count:
      data?.coverages?.filter(c => getCoverageDisplayStatus(c) === 'active').length || 0,
  };
}
